import { useCallback, useEffect, useRef, useState } from 'react'

type SpeechServerMessage = {
  type: 'ready' | 'partial' | 'transcript' | 'error'
  text?: string
  message?: string
}

export const useSpeechRecognition = (websocketUrl: string) => {
  const [isRecognizing, setIsRecognizing] = useState(false)
  const [isPreparing, setIsPreparing] = useState(false)
  const [liveTranscript, setLiveTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const muteGainRef = useRef<GainNode | null>(null)
  const audioChunkCountRef = useRef(0)
  const stopResolverRef = useRef<(() => void) | null>(null)
  const stopRecognitionRef = useRef<(() => void) | null>(null)
  const silenceTimerRef = useRef<number | null>(null)
  const hasDetectedSpeechRef = useRef(false)
  const completedTextRef = useRef('')
  const stopRequestedRef = useRef(false)

  const stopAudio = useCallback(async () => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current)
      silenceTimerRef.current = null
    }
    if (processorRef.current) {
      processorRef.current.onaudioprocess = null
      processorRef.current.disconnect()
      processorRef.current = null
    }
    sourceRef.current?.disconnect()
    sourceRef.current = null
    muteGainRef.current?.disconnect()
    muteGainRef.current = null
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    hasDetectedSpeechRef.current = false
    if (audioContextRef.current) {
      await audioContextRef.current.close().catch(() => undefined)
      audioContextRef.current = null
    }
  }, [])

  const startAudio = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    streamRef.current = stream
    const audioContext = new AudioContext({ sampleRate: 16000 })
    audioContextRef.current = audioContext
    if (audioContext.sampleRate !== 16000) {
      throw new Error(`瀏覽器音訊取樣率為 ${audioContext.sampleRate} Hz，Azure 需要 16000 Hz。`)
    }

    const source = audioContext.createMediaStreamSource(stream)
    const processor = audioContext.createScriptProcessor(256, 1, 1)
    const muteGain = audioContext.createGain()
    muteGain.gain.value = 0
    sourceRef.current = source
    processorRef.current = processor
    muteGainRef.current = muteGain

    processor.onaudioprocess = (event) => {
      const socket = wsRef.current
      if (!socket || socket.readyState !== WebSocket.OPEN || stopRequestedRef.current) return

      const samples = event.inputBuffer.getChannelData(0)
      const pcm = new Int16Array(samples.length)
      let energy = 0
      for (let index = 0; index < samples.length; index += 1) {
        const sample = Math.max(-1, Math.min(1, samples[index]))
        pcm[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
        energy += sample * sample
      }
      socket.send(pcm.buffer)
      audioChunkCountRef.current += 1

      const rms = Math.sqrt(energy / samples.length)
      if (rms >= 0.015) {
        hasDetectedSpeechRef.current = true
        if (silenceTimerRef.current !== null) {
          window.clearTimeout(silenceTimerRef.current)
          silenceTimerRef.current = null
        }
      } else if (hasDetectedSpeechRef.current && silenceTimerRef.current === null) {
        silenceTimerRef.current = window.setTimeout(() => {
          silenceTimerRef.current = null
          void stopRecognitionRef.current?.()
        }, 5000)
      }
    }

    source.connect(processor)
    processor.connect(muteGain)
    muteGain.connect(audioContext.destination)
  }, [])

  const stopRecognition = useCallback(async () => {
    if (stopRequestedRef.current) return
    const socket = wsRef.current
    if (!socket) {
      await stopAudio()
      setIsRecognizing(false)
      setIsPreparing(false)
      return
    }

    stopRequestedRef.current = true
    setIsRecognizing(false)
    setIsPreparing(true)
    await stopAudio()

    if (socket.readyState === WebSocket.OPEN && audioChunkCountRef.current > 0) {
      await new Promise<void>((resolve) => {
        let finished = false
        const finish = () => {
          if (finished) return
          finished = true
          window.clearTimeout(timeout)
          stopResolverRef.current = null
          resolve()
        }
        const timeout = window.setTimeout(finish, 5000)
        stopResolverRef.current = finish
        socket.send(JSON.stringify({ type: 'commit' }))
      })
    }

    if (socket.readyState < WebSocket.CLOSING) socket.close()
    if (wsRef.current === socket) wsRef.current = null
    audioChunkCountRef.current = 0
    setIsPreparing(false)
  }, [stopAudio])

  const startRecognition = useCallback(async () => {
    if (isRecognizing || isPreparing) return

    setError(null)
    setIsPreparing(true)
    setLiveTranscript('')
    completedTextRef.current = ''
    stopRequestedRef.current = false
    audioChunkCountRef.current = 0

    const socket = new WebSocket(websocketUrl)
    wsRef.current = socket

    await new Promise<void>((resolve, reject) => {
      let started = false
      const fail = (message: string) => {
        setError(message)
        setIsRecognizing(false)
        setIsPreparing(false)
        void stopAudio()
        if (wsRef.current === socket) wsRef.current = null
        if (socket.readyState < WebSocket.CLOSING) socket.close()
        if (!started) reject(new Error(message))
      }

      socket.onmessage = async (event) => {
        let data: SpeechServerMessage
        try {
          data = JSON.parse(event.data) as SpeechServerMessage
        } catch {
          fail('語音服務回傳了無法解析的訊息。')
          return
        }

        if (data.type === 'ready' && !started) {
          try {
            await startAudio()
            started = true
            setIsRecognizing(true)
            setIsPreparing(false)
            resolve()
          } catch (cause) {
            fail(cause instanceof Error ? cause.message : '無法啟用麥克風。')
          }
        } else if (data.type === 'transcript' && data.text) {
          completedTextRef.current += data.text
          setLiveTranscript(completedTextRef.current)
          stopResolverRef.current?.()
        } else if (data.type === 'partial') {
          setLiveTranscript(completedTextRef.current + (data.text || ''))
        } else if (data.type === 'error') {
          fail(data.message || 'Azure 語音辨識發生錯誤。')
          stopResolverRef.current?.()
        }
      }

      socket.onerror = () => fail('語音 WebSocket 連線失敗。')
      socket.onclose = () => {
        if (wsRef.current === socket && !stopRequestedRef.current) {
          setIsRecognizing(false)
          setIsPreparing(false)
          wsRef.current = null
          void stopAudio()
        }
        if (!started) reject(new Error('語音 WebSocket 在辨識準備完成前已關閉。'))
      }
    })
  }, [isPreparing, isRecognizing, startAudio, stopAudio, websocketUrl])

  useEffect(() => {
    stopRecognitionRef.current = stopRecognition
  }, [stopRecognition])

  useEffect(() => () => {
    stopRequestedRef.current = true
    stopResolverRef.current?.()
    if (wsRef.current && wsRef.current.readyState < WebSocket.CLOSING) wsRef.current.close()
    void stopAudio()
  }, [stopAudio])

  return {
    isRecognizing,
    isPreparing,
    liveTranscript,
    error,
    startRecognition,
    stopRecognition,
  }
}
